// Exports: useTaskGroups
import { useCallback, useEffect, useRef, useState } from "react";
import { useAlert } from "components/UI_components/Alert";
import useTaskApi from "hooks/useTaskApi";
import {
  deriveSubtaskStatus,
  getTaskDisplayStatus,
  isSubtaskCompleted,
  mergeUniqueMembers,
  priorityText,
  todayISO,
} from "utils/tasks/taskHelpers";
import {
  normalizeGroup,
  normalizeSubtask,
  normalizeTask,
} from "utils/tasks/taskNormalizers";

const TASK_GROUPS_CACHE_KEY = "taskGroups";
const EXPANDED_GROUPS_KEY = "expandedGroups";
const EXPANDED_TASKS_KEY = "expandedTasks";

const STATUS_OPTIONS = [
  { label: "Ongoing", color: "bg-yellow-400 text-gray-900" },
  { label: "Complete", color: "bg-green-500 text-white" },
  { label: "Halted", color: "bg-red-500 text-white" },
  { label: "Not started", color: "bg-gray-400 text-white" },
];

const readStorageJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    console.warn(`Failed to read ${key} from localStorage`, error);
    return fallback;
  }
};

const hydrateCachedGroups = () => {
  const cachedGroups = readStorageJSON(TASK_GROUPS_CACHE_KEY, []);
  const expandedGroups = readStorageJSON(EXPANDED_GROUPS_KEY, {});
  const expandedTasks = readStorageJSON(EXPANDED_TASKS_KEY, {});

  if (!Array.isArray(cachedGroups)) return [];

  return cachedGroups
    .filter((group) => group?.id && group?.name)
    .map((group) => ({
      ...group,
      expanded: Boolean(expandedGroups[group.id]),
      tasksFetched:
        group?.tasksFetched || (Array.isArray(group?.tasks) && group.tasks.length > 0),
      tasks: Array.isArray(group?.tasks)
        ? group.tasks.map((task) => ({
            ...task,
            expanded: Boolean(expandedTasks[task.id]),
            subtasksFetched:
              task?.subtasksFetched || (Array.isArray(task?.subtasks) && task.subtasks.length > 0),
            subtasks: Array.isArray(task?.subtasks) ? task.subtasks : [],
            subtasks_summary: task?.subtasks_summary || {
              total_subtasks: Array.isArray(task?.subtasks) ? task.subtasks.length : 0,
              completed_subtasks: Array.isArray(task?.subtasks)
                ? task.subtasks.filter(isSubtaskCompleted).length
                : 0,
            },
          }))
        : [],
    }));
};

const buildExpandedMaps = (groups) => {
  const expandedGroups = {};
  const expandedTasks = {};

  groups.forEach((group) => {
    if (group?.expanded) {
      expandedGroups[group.id] = true;
    }

    (group?.tasks || []).forEach((task) => {
      if (task?.expanded) {
        expandedTasks[task.id] = true;
      }
    });
  });

  return { expandedGroups, expandedTasks };
};

const countTaskStatuses = (tasks) => {
  const counts = {
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    overdue: 0,
  };

  tasks.forEach((task) => {
    const status = getTaskDisplayStatus(task);

    if (status === "Completed" || status === "Complete") {
      counts.completed += 1;
      return;
    }

    if (status === "Overdue" || status === "Halted") {
      counts.overdue += 1;
      return;
    }

    if (status === "Not started") {
      counts.notStarted += 1;
      return;
    }

    counts.inProgress += 1;
  });

  return counts;
};

const countTaskPriorities = (tasks) => {
  const counts = {
    high: 0,
    medium: 0,
    low: 0,
  };

  tasks.forEach((task) => {
    const priority = priorityText(task?.priorityId || task?.priority);
    if (priority === "High") counts.high += 1;
    else if (priority === "Low") counts.low += 1;
    else counts.medium += 1;
  });

  return counts;
};

const toPercentages = (counts, total) => {
  if (!total) {
    return Object.keys(counts).reduce((result, key) => {
      result[key] = 0;
      return result;
    }, {});
  }

  return Object.keys(counts).reduce((result, key) => {
    result[key] = (counts[key] / total) * 100;
    return result;
  }, {});
};

const recalculateTaskSummary = (task) => {
  const subtasks = Array.isArray(task?.subtasks) ? task.subtasks : [];

  // If subtasks haven't been fetched yet, and we have a summary with total > 0,
  // it means the summary came from the API and we should preserve it.
  if (
    !task.subtasksFetched &&
    task.subtasks_summary?.total_subtasks > 0 &&
    subtasks.length === 0
  ) {
    return task;
  }

  return {
    ...task,
    subtasks_summary: {
      total_subtasks: subtasks.length,
      completed_subtasks: subtasks.filter(isSubtaskCompleted).length,
    },
  };
};

const recalculateGroup = (group) => {
  const tasks = Array.isArray(group?.tasks) ? group.tasks.map(recalculateTaskSummary) : [];
  if (!group?.tasksFetched && tasks.length === 0) {
    return {
      ...group,
      tasks,
    };
  }

  const total = tasks.length;
  const statusCounts = countTaskStatuses(tasks);
  const priorityCounts = countTaskPriorities(tasks);

  return {
    ...group,
    tasks,
    statusPercentages: {
      completed: toPercentages(statusCounts, total).completed,
      inProgress: toPercentages(statusCounts, total).inProgress,
      notStarted: toPercentages(statusCounts, total).notStarted,
      overdue: toPercentages(statusCounts, total).overdue,
    },
    priorityPercentages: {
      high: toPercentages(priorityCounts, total).high,
      medium: toPercentages(priorityCounts, total).medium,
      low: toPercentages(priorityCounts, total).low,
    },
    allocated_members: mergeUniqueMembers(
      group?.allocated_members || [],
      tasks.flatMap((task) => task?.members || [])
    ),
  };
};

const findTaskContext = (groups, taskId) => {
  for (const group of groups) {
    const task = (group?.tasks || []).find((candidate) => candidate.id === taskId);
    if (task) {
      return { group, task };
    }
  }

  return null;
};

const findSubtaskContext = (groups, subtaskId) => {
  for (const group of groups) {
    for (const task of group?.tasks || []) {
      const subtask = (task?.subtasks || []).find(
        (candidate) => candidate.id === subtaskId
      );
      if (subtask) {
        return { group, task, subtask };
      }
    }
  }

  return null;
};

const buildTaskPayload = (task, overrides = {}) => ({
  task_name: task?.name || "",
  task_status: task?.status || "Ongoing",
  task_details: task?.task_details || "–",
  priority: task?.priorityId || 2,
  start_date: task?.start_date || todayISO(),
  end_date: task?.end_date || todayISO(),
  estimated_amount: task?.estimated_amount || 0,
  task_group: task?.task_group,
  ...overrides,
});

const buildSubtaskPayload = (subtask, overrides = {}) => ({
  subtask_name: subtask?.name || "",
  subtask_details: subtask?.subtask_details || "–",
  end_date: subtask?.end_date || todayISO(),
  start_date: subtask?.start_date || todayISO(),
  is_completed: Boolean(subtask?.is_completed),
  assign_to: Array.isArray(subtask?.assignee)
    ? subtask.assignee.map((assignee) => assignee?.member_id ?? assignee?.id)
    : [],
  ...overrides,
});

const useTaskGroups = (siteId, authHeaders) => {
  const { addAlert } = useAlert();
  const {
    addTaskGroup: createTaskGroup,
    addTask: createTask,
    addSubtask: createSubtask,
    updateTaskGroup,
    updateTask,
    updateSubtask,
    deleteSubtask: removeSubtask,
    fetchTasksForGroup: fetchGroupTasks,
    fetchSubtasksForTask: fetchTaskSubtasks,
    statePersistentRefresh: refreshTaskGroups,
    hasPermissionDetail,
  } = useTaskApi();

  const [taskGroups, setTaskGroups] = useState(hydrateCachedGroups);
  const [refreshing, setRefreshing] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [openPriorityDropdown, setOpenPriorityDropdown] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState("");
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubName, setEditingSubName] = useState("");

  const taskGroupsRef = useRef(taskGroups);
  void authHeaders;

  useEffect(() => {
    taskGroupsRef.current = taskGroups;
  }, [taskGroups]);

  useEffect(() => {
    localStorage.setItem(TASK_GROUPS_CACHE_KEY, JSON.stringify(taskGroups));
  }, [taskGroups]);

  useEffect(() => {
    const { expandedGroups, expandedTasks } = buildExpandedMaps(taskGroups);
    localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(expandedGroups));
    localStorage.setItem(EXPANDED_TASKS_KEY, JSON.stringify(expandedTasks));
  }, [taskGroups]);

  const statePersistentRefresh = useCallback(async () => {
    if (!siteId) return [];

    setRefreshing(true);
    try {
      const { expandedGroups, expandedTasks } = buildExpandedMaps(
        taskGroupsRef.current
      );
      const groups = await refreshTaskGroups(
        siteId,
        expandedGroups,
        expandedTasks
      );
      setTaskGroups(groups);
      return groups;
    } finally {
      setRefreshing(false);
    }
  }, [refreshTaskGroups, siteId]);

  useEffect(() => {
    statePersistentRefresh();
  }, [statePersistentRefresh]);

  const fetchTasksForGroup = useCallback(
    async (groupId) => {
      const currentGroup = taskGroupsRef.current.find((group) => group.id === groupId);
      if (!currentGroup) return [];

      const expandedTasks = (currentGroup.tasks || []).reduce((result, task) => {
        if (task?.expanded) {
          result[task.id] = true;
        }
        return result;
      }, {});

      const tasks = await fetchGroupTasks(groupId, expandedTasks);
      const hydratedTasks = await Promise.all(
        tasks.map(async (task) => {
          if (!task.expanded) return task;

          const subtasks = await fetchTaskSubtasks(groupId, task, {
            shouldAlert: false,
          });

          return recalculateTaskSummary({
            ...task,
            subtasks,
            subtasksFetched: true,
          });
        })
      );

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;
          return recalculateGroup({
            ...group,
            tasks: hydratedTasks,
            tasksFetched: true,
          });
        })
      );

      return hydratedTasks;
    },
    [fetchGroupTasks, fetchTaskSubtasks]
  );

  const fetchSubtasksForTask = useCallback(
    async (groupId, taskId) => {
      const context = findTaskContext(taskGroupsRef.current, taskId);
      if (!context) return [];

      const subtasks = await fetchTaskSubtasks(groupId, context.task);
      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    subtasks,
                    subtasksFetched: true,
                  })
                : task
            ),
          });
        })
      );

      return subtasks;
    },
    [fetchTaskSubtasks]
  );

  const onToggleGroup = useCallback(
    (event, groupId) => {
      event?.stopPropagation?.();

      const shouldFetch = (() => {
        const group = taskGroupsRef.current.find((candidate) => candidate.id === groupId);
        return group && !group.expanded && !group.tasksFetched;
      })();

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) =>
          group.id === groupId
            ? {
                ...group,
                expanded: !group.expanded,
              }
            : group
        )
      );

      if (shouldFetch) {
        fetchTasksForGroup(groupId);
      }
    },
    [fetchTasksForGroup]
  );

  const onToggleTask = useCallback(
    (event, groupId, taskId) => {
      event?.stopPropagation?.();

      const shouldFetch = (() => {
        const context = findTaskContext(taskGroupsRef.current, taskId);
        return (
          context &&
          !context.task.expanded &&
          (!Array.isArray(context.task.subtasks) || context.task.subtasks.length === 0)
        );
      })();

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return {
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? {
                    ...task,
                    expanded: !task.expanded,
                  }
                : task
            ),
          };
        })
      );

      if (shouldFetch) {
        fetchSubtasksForTask(groupId, taskId);
      }
    },
    [fetchSubtasksForTask]
  );

  const addTaskGroup = useCallback(
    async (taskData) => {
      if (!taskData?.name?.trim()) return null;

      const response = await createTaskGroup({
        site_id: siteId,
        task_group_name: taskData.name.trim(),
        status: "Active",
        start_date: todayISO(),
        end_date: taskData.dueDate || todayISO(),
        priority: 3,
      });

      if (hasPermissionDetail(response)) {
        addAlert(
          "You do not have permission to create this task group.",
          "error"
        );
        return null;
      }

      if (response?.status === false) {
        addAlert("Could not create task group.", "error");
        return null;
      }

      const newGroup = normalizeGroup(response?.data || response);
      if (!newGroup) return null;

      setTaskGroups((previousGroups) => [...previousGroups, newGroup]);
      addAlert(`Created new task group ${taskData.name.trim()}`, "success");
      return newGroup;
    },
    [addAlert, createTaskGroup, hasPermissionDetail, siteId]
  );

  const handleAddTask = useCallback(
    async (taskData) => {
      if (!taskData?.name?.trim() || !taskData?.tgId) return null;

      const response = await createTask({
        task_group: taskData.tgId,
        task_name: taskData.name.trim(),
        task_details: taskData.task_details || "–",
        task_status: "Not started",
        priority: 1,
        estimated_amount: taskData.estimated_amount || "0",
      });

      if (hasPermissionDetail(response)) {
        addAlert(
          "You do not have permission to create this task.",
          "error"
        );
        return null;
      }

      if (response?.status === false) {
        addAlert("Could not create task.", "error");
        return null;
      }

      const normalizedTask = normalizeTask(
        {
          ...(response?.data || response),
          task_name: response?.data?.task_name || taskData.name.trim(),
          task_group: taskData.tgId,
          task_status: response?.data?.task_status || "Not started",
        },
        taskData.tgId
      );

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== taskData.tgId) return group;

          return recalculateGroup({
            ...group,
            tasksFetched: true,
            tasks: [...(group.tasks || []), normalizedTask],
          });
        })
      );

      addAlert(`Successfully created task ${taskData.name.trim()}`, "success");
      return normalizedTask;
    },
    [addAlert, createTask, hasPermissionDetail]
  );

  const handleAddSubtask = useCallback(
    async (taskData) => {
      const taskId = taskData?.taskId ?? taskData?.task_id;
      const groupId = taskData?.tgId;
      const subtaskName = taskData?.subtask_name?.trim();

      if (!groupId || !taskId || !subtaskName) return null;

      const context = findTaskContext(taskGroupsRef.current, taskId);
      if (!context) return null;

      const response = await createSubtask({
        task_id: taskId,
        subtask_name: subtaskName,
        subtask_details: taskData?.subtask_details || null,
        assign_to: taskData?.assign_to || [],
      });

      if (hasPermissionDetail(response)) {
        addAlert(
          "You do not have permission to create this subtask.",
          "error"
        );
        return null;
      }

      const normalizedSubtask = normalizeSubtask(
        {
          ...(response?.data || response),
          subtask_name: response?.subtask_name || subtaskName,
          task_id: taskId,
          assign_to: taskData?.assign_to || [],
        },
        groupId,
        context.task
      );

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    subtasks: [...(task.subtasks || []), normalizedSubtask],
                    subtasksFetched: true,
                  })
                : task
            ),
          });
        })
      );

      addAlert(`Successfully created subtask ${subtaskName}`, "success");
      return normalizedSubtask;
    },
    [addAlert, createSubtask, hasPermissionDetail]
  );

  const deleteSubtask = useCallback(
    async (groupId, taskId, subtaskId) => {
      const snapshot = taskGroupsRef.current;

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    subtasks: (task.subtasks || []).filter(
                      (subtask) => subtask.id !== subtaskId
                    ),
                    subtasksFetched: true,
                  })
                : task
            ),
          });
        })
      );

      try {
        await removeSubtask(subtaskId);
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [removeSubtask]
  );

  const onGroupEditStart = useCallback((group) => {
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
  }, []);

  const onGroupEditCancel = useCallback(() => {
    setEditingGroupId(null);
    setEditingGroupName("");
  }, []);

  const onGroupEditSubmit = useCallback(
    async (nextName) => {
      const taskGroupName = String(nextName || editingGroupName).trim();
      if (!taskGroupName) {
        addAlert("Task group name cannot be empty.", "error");
        return;
      }

      const snapshot = taskGroupsRef.current;
      const targetGroupId = editingGroupId;
      if (!targetGroupId) return;

      setEditingGroupId(null);
      setEditingGroupName("");
      setTaskGroups((previousGroups) =>
        previousGroups.map((group) =>
          group.id === targetGroupId
            ? {
                ...group,
                name: taskGroupName,
              }
            : group
        )
      );

      try {
        const response = await updateTaskGroup(targetGroupId, {
          task_group_name: taskGroupName,
        });

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this task group name.",
            "error"
          );
          setTaskGroups(snapshot);
        }
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [
      addAlert,
      editingGroupId,
      editingGroupName,
      hasPermissionDetail,
      updateTaskGroup,
    ]
  );

  const onTaskEditStart = useCallback((task) => {
    setEditingTaskId(task.id);
    setEditingTaskName(task.name);
  }, []);

  const onTaskEditCancel = useCallback(() => {
    setEditingTaskId(null);
    setEditingTaskName("");
  }, []);

  const onTaskEditSubmit = useCallback(
    async (nextName) => {
      const taskName = String(nextName || editingTaskName).trim();
      if (!taskName) {
        addAlert("Task name cannot be empty.", "error");
        return;
      }

      const taskId = editingTaskId;
      if (!taskId) return;

      const snapshot = taskGroupsRef.current;
      const context = findTaskContext(snapshot, taskId);
      if (!context) return;

      setEditingTaskId(null);
      setEditingTaskName("");
      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== context.group.id) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    name: taskName,
                  })
                : task
            ),
          });
        })
      );

      try {
        const response = await updateTask(
          taskId,
          buildTaskPayload(context.task, {
            task_name: taskName,
          })
        );

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this task name.You must have Task Authority permission",
            "error"
          );
          setTaskGroups(snapshot);
        }
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, editingTaskId, editingTaskName, hasPermissionDetail, updateTask]
  );

  const handleTaskDateChange = useCallback(
    async (taskId, newStartDate, newEndDate) => {
      if (!newStartDate || !newEndDate) return;

      const snapshot = taskGroupsRef.current;
      const context = findTaskContext(snapshot, taskId);
      if (!context) return;

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== context.group.id) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    start_date: newStartDate,
                    end_date: newEndDate,
                    dueDate: newEndDate,
                  })
                : task
            ),
          });
        })
      );

      try {
        const response = await updateTask(
          taskId,
          buildTaskPayload(context.task, {
            start_date: newStartDate,
            end_date: newEndDate,
          })
        );

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this task dates.",
            "error"
          );
          setTaskGroups(snapshot);
          return;
        }

        addAlert("Task dates updated successfully.", "success");
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, hasPermissionDetail, updateTask]
  );

  const onSubEditStart = useCallback((subtask) => {
    setEditingSubId(subtask.id);
    setEditingSubName(subtask.name);
  }, []);

  const onSubEditCancel = useCallback(() => {
    setEditingSubId(null);
    setEditingSubName("");
  }, []);

  const onSubEditSubmit = useCallback(
    async (nextName) => {
      const subtaskName = String(nextName || editingSubName).trim();
      if (!subtaskName) {
        addAlert("Subtask name cannot be empty.", "error");
        return;
      }

      const subtaskId = editingSubId;
      if (!subtaskId) return;

      const snapshot = taskGroupsRef.current;
      const context = findSubtaskContext(snapshot, subtaskId);
      if (!context) return;

      setEditingSubId(null);
      setEditingSubName("");
      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== context.group.id) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === context.task.id
                ? recalculateTaskSummary({
                    ...task,
                    subtasks: task.subtasks.map((subtask) =>
                      subtask.id === subtaskId
                        ? {
                            ...subtask,
                            name: subtaskName,
                          }
                        : subtask
                    ),
                    subtasksFetched: true,
                  })
                : task
            ),
          });
        })
      );

      try {
        const response = await updateSubtask(
          subtaskId,
          buildSubtaskPayload(context.subtask, {
            subtask_name: subtaskName,
          })
        );

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this subtask.",
            "error"
          );
          setTaskGroups(snapshot);
        }
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, editingSubId, editingSubName, hasPermissionDetail, updateSubtask]
  );

  const handleSubtaskDateChange = useCallback(
    async (subtaskId, newStartDate, newEndDate) => {
      if (!newStartDate || !newEndDate) return;

      const snapshot = taskGroupsRef.current;
      const context = findSubtaskContext(snapshot, subtaskId);
      if (!context) return;

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== context.group.id) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === context.task.id
                ? recalculateTaskSummary({
                    ...task,
                    subtasks: task.subtasks.map((subtask) =>
                      subtask.id === subtaskId
                        ? {
                            ...subtask,
                            start_date: newStartDate,
                            end_date: newEndDate,
                            dueDate: newEndDate,
                            status: deriveSubtaskStatus(
                              {
                                ...subtask,
                                start_date: newStartDate,
                                end_date: newEndDate,
                              },
                              context.task.status
                            ),
                          }
                        : subtask
                    ),
                    subtasksFetched: true,
                  })
                : task
            ),
          });
        })
      );

      try {
        const response = await updateSubtask(
          subtaskId,
          buildSubtaskPayload(context.subtask, {
            start_date: newStartDate,
            end_date: newEndDate,
          })
        );

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this subtask dates.",
            "error"
          );
          setTaskGroups(snapshot);
          return;
        }

        addAlert("Subtask dates updated successfully.", "success");
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, hasPermissionDetail, updateSubtask]
  );

  const onTaskStatusChange = useCallback(
    async (groupId, taskId, newStatus) => {
      const snapshot = taskGroupsRef.current;
      const context = findTaskContext(snapshot, taskId);
      if (!context) return;

      setOpenStatusDropdown(null);
      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    status: newStatus,
                    subtasks: task.subtasks.map((subtask) => ({
                      ...subtask,
                      status: subtask.is_completed
                        ? "Completed"
                        : newStatus === "Not started"
                          ? "Not started"
                          : deriveSubtaskStatus(subtask, newStatus),
                    })),
                    subtasksFetched: task.subtasksFetched || task.subtasks.length > 0,
                  })
                : task
            ),
          });
        })
      );

      try {
        const response = await updateTask(
          taskId,
          buildTaskPayload(context.task, {
            task_status: newStatus,
          })
        );

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this task status.",
            "error"
          );
          setTaskGroups(snapshot);
        }
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, hasPermissionDetail, updateTask]
  );

  const onTaskPriorityChange = useCallback(
    async (groupId, taskId, newPriority) => {
      const snapshot = taskGroupsRef.current;
      const context = findTaskContext(snapshot, taskId);
      if (!context) return;

      setOpenPriorityDropdown(null);
      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    priorityId: newPriority,
                    priority: priorityText(newPriority),
                  })
                : task
            ),
          });
        })
      );

      try {
        const response = await updateTask(
          taskId,
          buildTaskPayload(context.task, {
            priority: newPriority,
          })
        );

        if (hasPermissionDetail(response)) {
          addAlert(
            "You do not have permission to update this task priority.",
            "error"
          );
          setTaskGroups(snapshot);
        }
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, hasPermissionDetail, updateTask]
  );

  const onToggleSubComplete = useCallback(
    async (groupId, taskId, subtaskId, isChecked) => {
      const snapshot = taskGroupsRef.current;
      const context = findSubtaskContext(snapshot, subtaskId);
      if (!context) return;

      const optimisticSubtasks = context.task.subtasks.map((subtask) =>
        subtask.id === subtaskId
          ? {
              ...subtask,
              is_completed: isChecked,
              completed_on: isChecked ? todayISO() : null,
              status: isChecked
                ? "Completed"
                : deriveSubtaskStatus(subtask, context.task.status),
            }
          : subtask
      );

      const allSubtasksCompleted =
        optimisticSubtasks.length > 0 &&
        optimisticSubtasks.every((subtask) => isSubtaskCompleted(subtask));
      const nextTaskStatus = allSubtasksCompleted ? "Complete" : "Ongoing";

      setTaskGroups((previousGroups) =>
        previousGroups.map((group) => {
          if (group.id !== groupId) return group;

          return recalculateGroup({
            ...group,
            tasks: group.tasks.map((task) =>
              task.id === taskId
                ? recalculateTaskSummary({
                    ...task,
                    status: nextTaskStatus,
                    subtasks: optimisticSubtasks,
                    subtasksFetched: true,
                  })
                : task
            ),
          });
        })
      );

      try {
        const subtaskResponse = await updateSubtask(
          subtaskId,
          buildSubtaskPayload(context.subtask, {
            is_completed: isChecked,
          })
        );

        if (hasPermissionDetail(subtaskResponse)) {
          addAlert(
            "You do not have permission to update this subtask.",
            "error"
          );
          setTaskGroups(snapshot);
          return;
        }

        if (context.task.status !== nextTaskStatus) {
          const taskResponse = await updateTask(
            taskId,
            buildTaskPayload(context.task, {
              task_status: nextTaskStatus,
            }),
            {
              shouldAlert: false,
            }
          );

          if (hasPermissionDetail(taskResponse)) {
            addAlert(
              "You do not have permission to update this task status.",
              "error"
            );
            setTaskGroups(snapshot);
          }
        }
      } catch (error) {
        setTaskGroups(snapshot);
      }
    },
    [addAlert, hasPermissionDetail, updateSubtask, updateTask]
  );

  const mergeGroupAllocatedMembers = useCallback((groupId, members) => {
    if (!Array.isArray(members) || members.length === 0) return;

    setTaskGroups((previousGroups) =>
      previousGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              allocated_members: mergeUniqueMembers(
                group?.allocated_members || [],
                members
              ),
            }
          : group
      )
    );
  }, []);

  const getStatusProportions = useCallback((tasks) => {
    const total = tasks?.length || 1;
    const counts = {
      Ongoing: 0,
      Complete: 0,
      Overdue: 0,
      "Not started": 0,
    };

    (tasks || []).forEach((task) => {
      const status = getTaskDisplayStatus(task);
      if (status === "Completed") {
        counts.Complete += 1;
      } else if (counts[status] != null) {
        counts[status] += 1;
      } else {
        counts["Not started"] += 1;
      }
    });

    return {
      Ongoing: (counts.Ongoing / total) * 100,
      Complete: (counts.Complete / total) * 100,
      Overdue: (counts.Overdue / total) * 100,
      NotStarted: (counts["Not started"] / total) * 100,
    };
  }, []);

  return {
    taskGroups,
    refreshing,
    statusOptions: STATUS_OPTIONS,
    statePersistentRefresh,
    fetchTasksForGroup,
    fetchSubtasksForTask,
    onToggleGroup,
    onToggleTask,
    addTaskGroup,
    handleAddTask,
    handleAddSubtask,
    deleteSubtask,
    onGroupEditStart,
    onGroupEditCancel,
    onGroupEditSubmit,
    editingGroupId,
    editingGroupName,
    onTaskEditStart,
    onTaskEditCancel,
    onTaskEditSubmit,
    editingTaskId,
    editingTaskName,
    onSubEditStart,
    onSubEditCancel,
    onSubEditSubmit,
    editingSubId,
    editingSubName,
    onTaskStatusChange,
    onTaskPriorityChange,
    onToggleSubComplete,
    handleTaskDateChange,
    handleSubtaskDateChange,
    mergeGroupAllocatedMembers,
    openStatusDropdown,
    setOpenStatusDropdown,
    openPriorityDropdown,
    setOpenPriorityDropdown,
    getStatusProportions,
  };
};

export default useTaskGroups;
